import { useHistory, useRouteMatch } from 'react-router-dom';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isBoolean, isNil } from 'lodash/fp';
import { getUrlParams, setUrlParams } from '../../../../utils/helpers/urlParams';

type ParamType = boolean | false | string | null | number | string | string[];

const useSearchParams = () => {
    const history = useHistory();
    const { isExact } = useRouteMatch();

    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [stockCardCategoryId, setStockCardCategoryId] = useState<string | null>(null);
    const [stockCardNameOrSku, setStockCardNameOrSku] = useState('');
    const [filter, setFilter] = useState<string | null>(null);

    const handleUrlParamSet = <T extends ParamType>(setState: Dispatch<SetStateAction<T>>) =>
        (paramObj: Record<string, ParamType>) => {
            if (isExact) {
                const paramName = Object.keys(paramObj)[0];
                const param = paramObj[paramName];
                setState(param as T);
                let finalParam = isNil(param) ? '' : param || '';
                if (isBoolean(finalParam)) {
                    finalParam = finalParam ? 'true' : '';
                }
                const newUrlParams = setUrlParams(history.location.search, {
                    [paramName]: finalParam,
                });

                history.push({ search: newUrlParams.toString() });
            }
        };

    useEffect(() => {
        if (isExact) {
            const [
                rowsPerPageParam,
                pageParam,
                stockCardCategoryIdParam,
                stockCardNameOrSkuParam,
                filterParam,
            ] = getUrlParams({
                searchString: history.location.search,
                params: [
                    'rowsPerPage',
                    'page',
                    'stockCardCategoryId',
                    'stockCardNameOrSku',
                    'filter'
                ]
            });

            setRowsPerPage(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : 10);
            setPage(pageParam ? parseInt(pageParam, 10) : 0);
            setStockCardCategoryId(stockCardCategoryIdParam || null);
            setStockCardNameOrSku(stockCardNameOrSkuParam || '');
            setFilter(filterParam || null);

            const newUrlParams = setUrlParams(history.location.search, {
                rowsPerPage: rowsPerPageParam || 10,
                page: pageParam || 0,
                stockCardCategoryId: stockCardCategoryIdParam || '',
                stockCardNameOrSku: stockCardNameOrSkuParam || '',
                filter: filterParam || '',
            });

            history.push({ search: newUrlParams.toString() });
        }
    // eslint-disable-next-line
    }, [isExact]);

    const handleSetRowsPerPage = (p: ParamType) => handleUrlParamSet(setRowsPerPage)({ rowsPerPage: p });
    const handleSetPage = (r: ParamType) => handleUrlParamSet(setPage)({ page: r });
    const handleSetStockCardCategoryId = (p: ParamType) => handleUrlParamSet(setStockCardCategoryId)({ stockCardCategoryId: p });
    const handleSetStockCardNameOrSku = (p: ParamType) => handleUrlParamSet(setStockCardNameOrSku)({ lessThanExpectedOnly: p });
    const handleSetFilter = (p: ParamType) => handleUrlParamSet(setFilter)({ filter: p });

    return {
        rowsPerPage, setRowsPerPage: handleSetRowsPerPage,
        page, setPage: handleSetPage,
        stockCardCategoryId, setStockCardCategoryId: handleSetStockCardCategoryId,
        stockCardNameOrSku, setStockCardNameOrSku: handleSetStockCardNameOrSku,
        filter, setFilter: handleSetFilter,
    };
};

export default useSearchParams;
